import { Link } from 'react-router-dom';
import Button from '@/components/base/Button';
import { useSavedSubmission } from '@/hooks/useSavedSubmission';
import { CHECKLIST_DOWNLOADS } from '@/data/checklist';

const RESULT_KEY = 'kor38_checklist';

export default function FreeEventChecklistThankYou() {
  const { saved, ready } = useSavedSubmission(RESULT_KEY);

  return (
    <>
      <section className="w-full px-4 md:px-8 lg:px-12 pt-36 pb-12 md:pt-48 md:pb-16 bg-background-50">
        <div className="max-w-content mx-auto max-w-3xl">
          <span className="font-label text-xs uppercase tracking-widest2 text-primary-400">
            Your Complimentary Event Guide
          </span>
          <h1 className="mt-5 font-heading text-3xl md:text-5xl leading-[1.1] text-foreground-950">
            Your Event Supplier Checklist Is Ready!
          </h1>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-foreground-700">
            Thank you for requesting your complimentary KOR38 guide. Choose the version that works
            best for you.
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 pb-14 md:pb-20 bg-background-50">
        <div className="max-w-content mx-auto max-w-3xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {CHECKLIST_DOWNLOADS.map((download) => (
              <div
                key={download.id}
                className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-6"
              >
                <span className="w-11 h-11 rounded-md bg-secondary-900 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-xl text-primary-400" aria-hidden="true"></i>
                </span>
                <h2 className="mt-5 font-heading text-lg text-foreground-950">{download.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700 flex-1">
                  {download.description}
                </p>
                <div className="mt-6">
                  {download.href ? (
                    <Button
                      href={download.href}
                      download={download.fileName}
                      variant="primary"
                      size="lg"
                      icon="ri-download-2-line"
                      className="w-full"
                    >
                      {download.cta}
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-6 text-sm leading-relaxed text-foreground-600">
            Both versions include three supplier comparison worksheets.
          </p>

          {ready ? (
            <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              {saved ? (
                saved.marketingOptIn ? (
                  <p className="flex items-start gap-3 text-base leading-relaxed text-foreground-800">
                    <i className="ri-mail-check-line mt-0.5 text-xl text-primary-400" aria-hidden="true"></i>
                    <span>
                      You have opted in to receive useful event supplier tips and updates from KOR38.
                      You can unsubscribe at any time.
                    </span>
                  </p>
                ) : (
                  <p className="flex items-start gap-3 text-base leading-relaxed text-foreground-800">
                    <i className="ri-mail-close-line mt-0.5 text-xl text-primary-400" aria-hidden="true"></i>
                    <span>
                      You have not opted in to receive marketing emails. You can still download both
                      versions of your free checklist.
                    </span>
                  </p>
                )
              ) : (
                <p className="flex items-start gap-3 text-base leading-relaxed text-foreground-600">
                  <i className="ri-information-line mt-0.5 text-xl text-primary-400" aria-hidden="true"></i>
                  <span>
                    We don&rsquo;t have a record of a request from this device. You can still download
                    both versions of the checklist, or request it using the link below.
                  </span>
                </p>
              )}
            </div>
          ) : null}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 pb-20 md:pb-28 bg-background-50">
        <div className="max-w-content mx-auto max-w-3xl">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
            <h2 className="font-heading text-2xl md:text-3xl leading-snug text-foreground-950">
              Need help finding the right people for your event?
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              KOR38 offers a personal approach to supplier sourcing and introductions — from a single
              specialist to a full team of suppliers.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
              <Button to="/services" variant="primary" size="lg" className="w-full sm:w-auto">
                Explore Our Services
              </Button>
              <Link
                to="/free-event-checklist"
                className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-300 transition-colors duration-300"
              >
                <i className="ri-arrow-left-line" aria-hidden="true"></i>
                Request the checklist
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}